import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Package, Loader, ArrowRight, X, Check, RefreshCw, Search, Eye } from 'lucide-react';
import { getAllOrders, updateOrderStatus, markOrderDelivered, generateOrder } from './api/Label';
import { get_tracking_data_from_cloud } from './api/FirestoreUtils';
import { trackShipments } from './cart/Utils.js';
import PlaceOrder from './PlaceOrder';
import debounce from 'lodash/debounce';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredStatus, setFilteredStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [popupOrder, setPopupOrder] = useState(null);
  const [processingOrderId, setProcessingOrderId] = useState(null);
  const [exchangeTrackingId, setExchangeTrackingId] = useState('');
  const [exchangeSize, setExchangeSize] = useState('');
  const [trackingData, setTrackingData] = useState({});
  const [loadingTracking, setLoadingTracking] = useState({});
  const [showTracking, setShowTracking] = useState({});

  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const allOrders = await getAllOrders();
      const sortedOrders = allOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setOrders(sortedOrders);
      const uniqueStatuses = [...new Set(sortedOrders.map(order => order.orderStatus))];
      setStatuses(['All', ...uniqueStatuses]);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const fetchTrackingDataForOrder = useCallback(async (order) => {
    setLoadingTracking(prev => ({ ...prev, [order.id]: true }));
    try {
      const trackingResponse = await get_tracking_data_from_cloud(order.userEmail, order.id);
      const trackingDetails = trackingResponse.success && trackingResponse.data ? trackingResponse.data : [];

      setTrackingData(prev => ({
        ...prev,
        [order.id]: trackingDetails,
      }));

      const trackingNumbers = trackingDetails
        .map(t => t.trackingNumber)
        .filter(trackingNumber => trackingNumber && typeof trackingNumber === 'string' && trackingNumber.trim() !== '');

      if (trackingNumbers.length > 0) {
        try {
          const trackingResponse = await trackShipments(trackingNumbers);
          setTrackingData(prev => {
            const updatedTrackingData = { ...prev };
            trackingResponse.data.forEach((tracking) => {
              if (updatedTrackingData[order.id]?.some(t => t.trackingNumber === tracking.trackingNumber)) {
                updatedTrackingData[order.id] = updatedTrackingData[order.id].map(t =>
                  t.trackingNumber === tracking.trackingNumber ? { ...t, ...tracking } : t
                );
              }
            });
            return updatedTrackingData;
          });
        } catch (error) {
          console.error(`Error calling trackShipments for Order ${order.id}:`, error);
        }
      }
    } catch (error) {
      console.error(`Error fetching tracking data for Order ${order.id}:`, error);
      setTrackingData(prev => ({
        ...prev,
        [order.id]: [],
      }));
    } finally {
      setLoadingTracking(prev => ({
        ...prev,
        [order.id]: false,
      }));
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleShowTracking = (order) => {
    setShowTracking(prev => ({ ...prev, [order.id]: true }));
    if (!trackingData[order.id]) {
      fetchTrackingDataForOrder(order);
    }
  };

  const handleStatusChange = async (email, orderId, newStatus, trackingInfo) => {
    setProcessingOrderId(orderId);
    try {
      await updateOrderStatus(
        email,
        orderId,
        newStatus,
        trackingInfo.trackingNumber,
        trackingInfo.carrier
      );

      const updatedOrders = orders.map(order =>
        order.id === orderId && order.userEmail === email
          ? {
              ...order,
              orderStatus: newStatus,
              trackingId: trackingInfo.trackingNumber,
              carrier: trackingInfo.carrier,
            }
          : order
      );
      setOrders(updatedOrders);
      setPopupOrder(null);
      await fetchOrders();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleMarkDelivered = async (email, orderId) => {
    setProcessingOrderId(orderId);
    try {
      await markOrderDelivered(email, orderId);
      const updatedOrders = orders.map(order =>
        order.id === orderId && order.userEmail === email
          ? { ...order, orderStatus: 'Delivered', deliveredAt: new Date().toISOString() }
          : order
      );
      setOrders(updatedOrders);
      await fetchOrders();
    } catch (error) {
      console.error('Error marking delivered:', error);
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleCompleteReturn = async (email, orderId) => {
    setProcessingOrderId(orderId);
    try {
      await updateOrderStatus(email, orderId, 'Returned', null);
      const updatedOrders = orders.map(order =>
        order.id === orderId && order.userEmail === email
          ? { ...order, orderStatus: 'Returned' }
          : order
      );
      setOrders(updatedOrders);
      await fetchOrders();
    } catch (error) {
      console.error('Error completing return:', error);
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleAcceptExchange = (order) => {
    setPopupOrder(order);
  };

  const handleConfirmExchange = async (email, orderId, trackingInfo) => {
    setProcessingOrderId(orderId);
    try {
      await updateOrderStatus(
        email,
        orderId,
        'Returning',
        trackingInfo.trackingNumber,
        trackingInfo.carrier
      );

      const updatedOrders = orders.map(order =>
        order.id === orderId && order.userEmail === email
          ? {
              ...order,
              orderStatus: 'Returning',
              trackingId: trackingInfo.trackingNumber,
              carrier: trackingInfo.carrier,
            }
          : order
      );
      setOrders(updatedOrders);
      setPopupOrder(null);
      setExchangeTrackingId('');
      await fetchOrders();
    } catch (error) {
      console.error('Error processing exchange:', error);
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleCreateExchangeOrder = async (order) => {
    setProcessingOrderId(order.id);
    try {
      const orderDetails = {
        userEmail: order.userEmail,
        items: [{
          name: order.items[0].name,
          size: exchangeSize,
          color: order.items[0].color,
          quantity: 1,
          declaredValue: order.items[0].declaredValue,
        }],
        address: order.shippingAddress,
        totalPrice: order.totalPrice,
        paymentMethod: order.paymentMethod,
      };

      const result = await generateOrder(orderDetails);
      if (result.success) {
        await updateOrderStatus(order.userEmail, order.id, 'Exchanged', null);
        setPopupOrder(null);
        setExchangeSize('');
        await fetchOrders();
      }
    } catch (error) {
      console.error('Error creating exchange order:', error);
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleCopy = async (value) => {
    const str = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
    await navigator.clipboard.writeText(str);
  };

  const debouncedSetSearchQuery = useMemo(
    () => debounce((value) => setSearchQuery(value), 300),
    []
  );

  const filteredOrders = useMemo(() => {
    return orders
      .filter(order => 
        filteredStatus === 'All' || order.orderStatus === filteredStatus
      )
      .filter(order => 
        order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.userEmail.toLowerCase().includes(searchQuery.toLowerCase())
      );
  }, [orders, filteredStatus, searchQuery]);

  const getStatusStyle = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'delivered': return 'bg-green-700 text-white p-2 rounded-full';
      case 'shipped': return 'bg-blue-700 text-white p-2 rounded-full';
      case 'placed': return 'bg-yellow-500 text-black p-2 rounded-full';
      case 'cancelled': return 'bg-red-700 text-white p-2 rounded-full';
      case 'dispatched': return 'bg-yellow-700 text-white p-2 rounded-full';
      case 'exchanging': return 'bg-violet-700 text-white p-2 rounded-full';
      case 'returning': return 'bg-yellow-700 text-white p-2 rounded-full';
      case 'returned': return 'bg-orange-700 text-white p-2 rounded-full';
      case 'exchange': return 'bg-purple-700 text-white p-2 rounded-full';
      case 'exchanged': return 'bg-pink-700 text-white p-2 rounded-full';
      default: return 'bg-gray-600 text-white p-2 rounded-full';
    }
  };

  return (
    <div className="bg-[#111111] p-6 rounded-2xl shadow-lg text-[#DBAE6E] relative">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold">{filteredStatus} Orders</h1>
        <select
          value={filteredStatus}
          onChange={(e) => setFilteredStatus(e.target.value)}
          className="bg-black border border-[#DBAE6E] text-[#DBAE6E] px-4 py-2 rounded-lg text-sm"
        >
          {statuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            onChange={(e) => debouncedSetSearchQuery(e.target.value)}
            placeholder="Search by Order ID or Email"
            className="w-full bg-[#1a1a1a] border border-[#DBAE6E] text-[#DBAE6E] pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DBAE6E] placeholder-gray-500"
          />
        </div>
        <button
          onClick={fetchOrders}
          className="flex items-center gap-2 bg-[#DBAE6E] text-black px-4 py-2 rounded-lg hover:bg-[#e6c083] transition-all duration-200 hover:scale-105"
          disabled={loadingOrders}
        >
          {loadingOrders ? (
            <Loader className="animate-spin" size={18} />
          ) : (
            <RefreshCw size={18} />
          )}
          <span>Refresh</span>
        </button>
      </div>

      {loadingOrders ? (
        <div className="flex items-center gap-3 text-gray-400">
          <Loader className="animate-spin" size={24} /> Loading orders...
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="flex items-center gap-3 text-gray-400">
          <Package size={24} /> No orders found.
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div
              key={order.id}
              className="flex justify-between items-start border border-[#DBAE6E] rounded-xl p-4 bg-[#1a1a1a] shadow"
            >
              <div className="w-full">
                <p className="text-sm text-gray-400">#{order.id}</p>
                <div className="font-semibold text-lg">{order.userEmail}</div>
                <div className="text-sm text-white">
                  <p>{order.items.length} Item(s)</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {new Date(order.createdAt).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    }).replace(/\//g, '/')}
                  </p>
                </div>
                <div className="text-sm mt-2">
                  {order.orderStatus === 'Placed' && (
                    <button
                      className="ml-3 text-sm flex items-center gap-1 bg-[#DBAE6E] text-black px-3 py-1 rounded-lg hover:bg-[#e6c083] relative transition-all duration-200 hover:scale-105"
                      onClick={() => setPopupOrder(order)}
                      disabled={processingOrderId === order.id}
                    >
                      {processingOrderId === order.id && (
                        <Loader className="animate-spin absolute inset-0 m-auto" size={14} />
                      )}
                      <span className={processingOrderId === order.id ? 'opacity-0' : ''}>
                        <div className="flex p-2">
                          <ArrowRight size={14} /> Dispatch
                        </div>
                      </span>
                    </button>
                  )}
                  <div className="text-2xl font-bold text-left min-w-[100px]">
                    ₹{order.totalPrice}
                  </div>
                  {order.orderStatus === 'Dispatched' && (
                    <div className="mt-4">
                      {!showTracking[order.id] ? (
                        <button
                          className="ml-3 text-sm flex items-center gap-1 bg-[#DBAE6E] text-black px-3 py-1 rounded-lg hover:bg-[#e6c083] transition-all duration-200 hover:scale-105"
                          onClick={() => handleShowTracking(order)}
                        >
                          <Eye size={14} /> Show Tracking Details
                        </button>
                      ) : loadingTracking[order.id] ? (
                        <div className="flex justify-center items-center py-4">
                          <Loader className="animate-spin text-[#DBAE6E]" size={24} />
                        </div>
                      ) : trackingData[order.id]?.length > 0 ? (
                        <table className="w-full text-sm text-[#DBAE6E] border border-[#DBAE6E] rounded-lg">
                          <thead>
                            <tr className="bg-[#222]">
                              <th className="px-4 py-2 text-left">Product Name</th>
                              <th className="px-4 py-2 text-left">Status</th>
                              <th className="px-4 py-2 text-left">Tracking ID</th>
                              <th className="px-4 py-2 text-left">Carrier</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trackingData[order.id].map((tracking, index) => (
                              <tr key={index} className="border-t border-[#DBAE6E]">
                                <td className="px-4 py-2">
                                  {tracking.content?.content || tracking.packages?.[0]?.content || order.items[0]?.name || 'N/A'}
                                </td>
                                <td className="px-4 py-2">
                                  {tracking.status ? (
                                    <span
                                      className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(tracking.status)}`}
                                    >
                                      {tracking.status.toUpperCase()}
                                    </span>
                                  ) : (
                                    <Loader className="animate-spin text-[#DBAE6E]" size={14} />
                                  )}
                                </td>
                                <td className="px-4 py-2">
                                  {tracking.trackingNumber || <Loader className="animate-spin text-[#DBAE6E]" size={14} />}
                                </td>
                                <td className="px-4 py-2">
                                  {tracking.carrier || <Loader className="animate-spin text-[#DBAE6E]" size={14} />}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="text-gray-400">No tracking details available</p>
                      )}
                    </div>
                  )}
                  {order.orderStatus === 'Dispatched' && (
                    <button
                      className="ml-3 mt-4 text-sm flex items-center gap-1 bg-[#DBAE6E] text-black px-3 py-1 rounded-lg hover:bg-[#e6c083] relative transition-all duration-200 hover:scale-105"
                      onClick={() => handleMarkDelivered(order.userEmail, order.id)}
                      disabled={processingOrderId === order.id}
                    >
                      {processingOrderId === order.id && (
                        <Loader className="animate-spin absolute inset-0 m-auto" size={14} />
                      )}
                      <span className={processingOrderId === order.id ? 'opacity-0' : ''}>
                        <div className="flex p-2">
                          <ArrowRight size={14} /> Delivered
                        </div>
                      </span>
                    </button>
                  )}
                  {order.orderStatus === 'Exchanging' && (
                    <div className="ml-3 mt-2 bg-[#222] rounded-lg p-2">
                      <span className="text-sm">
                        <strong>Exchange for:</strong> {order.exchangeFor || 'Not specified'}
                      </span>
                      <button
                        className="ml-3 text-sm flex items-center gap-1 bg-[#DBAE6E] text-black px-3 py-1 rounded-lg hover:bg-[#e6c083] relative transition-all duration-200 hover:scale-105"
                        onClick={() => handleAcceptExchange(order)}
                        disabled={processingOrderId === order.id}
                      >
                        {processingOrderId === order.id && (
                          <Loader className="animate-spin absolute inset-0 m-auto" size={14} />
                        )}
                        <span className={processingOrderId === order.id ? 'opacity-0' : ''}>
                          <div className="flex">
                            <Check size={14} /> Accept
                          </div>
                        </span>
                      </button>
                    </div>
                  )}
                  {order.orderStatus === 'Returning' && (
                    <button
                      className="ml-3 text-sm flex items-center gap-1 bg-[#DBAE6E] text-black px-3 py-1 rounded-lg hover:bg-[#e6c083] relative transition-all duration-200 hover:scale-105"
                      onClick={() => handleCompleteReturn(order.userEmail, order.id)}
                      disabled={processingOrderId === order.id}
                    >
                      {processingOrderId === order.id && (
                        <Loader className="animate-spin absolute inset-0 m-auto" size={14} />
                      )}
                      <span className={processingOrderId === order.id ? 'opacity-0' : ''}>
                        <div className="flex">
                          <Check size={14} /> Complete Return
                        </div>
                      </span>
                    </button>
                  )}
                  {order.orderStatus === 'Returned' && (
                    <button
                      className="ml-3 text-sm flex items-center gap-1 bg-[#DBAE6E] text-black px-3 py-1 rounded-lg hover:bg-[#e6c083] relative transition-all duration-200 hover:scale-105"
                      onClick={() => setPopupOrder(order)}
                      disabled={processingOrderId === order.id}
                    >
                      {processingOrderId === order.id && (
                        <Loader className="animate-spin absolute inset-0 m-auto" size={14} />
                      )}
                      <span className={processingOrderId === order.id ? 'opacity-0' : ''}>
                        <div className="flex">
                          <ArrowRight size={14} /> Exchange for {order.exchangeFor}
                        </div>
                      </span>
                    </button>
                  )}
                  {order.trackingId && order.orderStatus !== 'Dispatched' && (
                    <span className="ml-3 text-sm py-2 text-gray-400">
                      <strong>Tracking ID:</strong> {order.trackingId}
                      {order.carrier && ` (${order.carrier})`}
                    </span>
                  )}
                  {order.orderStatus === 'Delivered' && order.deliveredAt && (
                    <span className="ml-3 text-sm text-gray-400">
                      <strong>Delivered:</strong> {new Date(order.deliveredAt).toLocaleString()}
                    </span>
                  )}
                </div>

                <div
                  className="text-sm mt-1 text-gray-400 cursor-pointer underline"
                  onClick={() =>
                    setExpandedOrderId(expandedOrderId === order.id ? null : order.id)
                  }
                >
                  {expandedOrderId === order.id ? 'Hide Address' : 'Show Address'}
                </div>

                {expandedOrderId === order.id && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-[#DBAE6E]">
                    {Object.entries(order.shippingAddress)
                      .filter(([key]) => !['shippingService', 'carrier'].includes(key))
                      .map(([key, value]) => (
                        <div
                          key={key}
                          className="bg-[#222] px-3 py-2 rounded-lg cursor-pointer hover:bg-[#333]"
                          onClick={() => handleCopy(value)}
                          title="Click to copy"
                        >
                          <strong className="capitalize">{key}</strong>: {typeof value === 'object' ? JSON.stringify(value) : value || '-'}
                        </div>
                      ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <div className="text-sm mt-3">
                    <span
                      className={
                        order.paymentMethod.toLowerCase() === 'cod'
                          ? 'text-red-400'
                          : 'text-green-400'
                      }
                    >
                      {order.paymentMethod.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-sm mt-3">
                    <span className={getStatusStyle(order.orderStatus)}>
                      {order.orderStatus.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {popupOrder && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6">
          <div className="bg-[#1a1a1a] border border-[#DBAE6E] p-6 rounded-2xl shadow-xl w-full max-w-2xl relative">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-white"
              onClick={() => setPopupOrder(null)}
            >
              <X size={22} />
            </button>
            {popupOrder.orderStatus === 'Returned' ? (
              <div className="space-y-4">
                <h2 className="text-xl font-bold">Create Exchange Order</h2>
                <div>
                  <p><strong>Item:</strong> {popupOrder.items[0].name}</p>
                  <p><strong>Size:</strong> {popupOrder.items[0].size}</p>
                  <p><strong>Exchange:</strong> {popupOrder.items[0].exchangeFor}</p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm">Select New Size:</label>
                  <select
                    value={exchangeSize}
                    onChange={(e) => setExchangeSize(e.target.value)}
                    className="w-full bg-[#222] border border-[#DBAE6E] text-[#DBAE6E] px-3 py-2 rounded-lg"
                  >
                    <option value="">Select size</option>
                    <option value="S">Small</option>
                    <option value="M">Medium</option>
                    <option value="L">Large</option>
                    <option value="XL">XL</option>
                  </select>
                </div>
                <button
                  className="flex items-center gap-1 bg-[#DBAE6E] text-black px-4 py-2 rounded-lg hover:bg-[#e6c083] relative transition-all duration-200 hover:scale-105"
                  onClick={() => handleCreateExchangeOrder(popupOrder)}
                  disabled={processingOrderId === popupOrder.id || !exchangeSize}
                >
                  {processingOrderId === popupOrder.id && (
                    <Loader className="animate-spin absolute inset-0 m-auto" size={18} />
                  )}
                  <span className={processingOrderId === popupOrder.id ? 'opacity-0' : ''}>
                    <div className="flex">
                      <Check size={18} /> Create Exchange Order
                    </div>
                  </span>
                </button>
              </div>
            ) : popupOrder.exchangeFor ? (
<div className="space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
  <h2 className="text-xl font-bold">Process Exchange</h2>
  <div>
    <p><strong>Exchange for:</strong> {popupOrder.exchangeFor}</p>
    <div className="mt-3 text-sm">
      <strong>Shipping Address:</strong>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
        {Object.entries(popupOrder.shippingAddress)
          .filter(([key]) => !['shippingService', 'carrier'].includes(key))
          .map(([key, value]) => (
            <div key={key} className="bg-[#222] px-3 py-2 rounded-lg">
              <strong className="capitalize">{key}</strong>: {typeof value === 'object' ? JSON.stringify(value) : value || '-'}
            </div>
          ))}
      </div>
    </div>
  </div>
  <PlaceOrder
    order={popupOrder}
    onConfirm={(trackingInfo) =>
      handleConfirmExchange(popupOrder.userEmail, popupOrder.id, trackingInfo)
    }
    isExchange={true}
  />
</div>
            ) : (
              <PlaceOrder
                order={popupOrder}
                onConfirm={(trackingInfo) =>
                  handleStatusChange(popupOrder.userEmail, popupOrder.id, 'Dispatched', trackingInfo)
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(AdminOrders);